import { and, assign, setup } from 'xstate';

import { AP_STATUS } from 'constants/payment/appointmentStatus.const';
import { AP_STATUS_FOR_STATE, APM_EVENT, APM_STATE } from 'constants/sidebar/sidebarAppointment.const';
import { firstOf } from 'utils/arrays/elements.util';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import { fromAnyDateToIso } from 'utils/converters/time/from.util';
import { eodAsIso, nowAsIso } from 'utils/date/now.util';
import call from 'utils/functions/call.util';
import logger from 'utils/logger.util';
import { isTimeAfterOrEqual, isTimeBefore, isTimeSameDay } from 'utils/predicates/time/compare.util';

const L = logger('appointmentMachine'); // eslint-disable-line no-unused-vars

const APM_TIME = Object.freeze({
  past: 'past',
  future: 'future',
});

const APM_ACTION = Object.freeze({
  displayConfirmation: 'actionDisplayConfirmation',
  deleteOne: 'actionDeleteOne',
  deleteAll: 'actionDeleteAll',
  init: 'actionInit',
  updateStatus: 'actionUpdateStatus',
});

const APM_GUARD = Object.freeze({
  isFuture: 'isFuture',
  isNowPast: 'isNowPast',
  isPast: 'isPast',

  isStatus: 'isStatus',
  isSkip: 'isSkip',
});

const APM_DOT = '.';
const APM_INIT_STATE = '?_??';
const APM_INIT_XSTATE_EVENT = 'xstate.init';
const APM_INIT_ENTRY = [{ type: APM_ACTION.init }];

const refreshFor = (tag) => {
  tag = fromAnyToTrimmed(tag);

  // noinspection UnnecessaryLocalVariableJS
  const refresh = ($) => {
    // { context, event, self, system } = $

    const ctx = $?.context;
    const input = $?.event?.input;
    const appointment = $?.event?.appointment;

    const appointmentId = input?.appointmentId ?? appointment?.id ?? $?.event?.appointmentId;
    const appointmentBgn = fromAnyDateToIso(input?.bgn) || fromAnyDateToIso(firstOf(appointment?.procedures)?.startTime) || void 1;

    const cutoff = fromAnyDateToIso(input?.cutoff) || eodAsIso();
    const future = isTimeAfterOrEqual(appointmentBgn, cutoff);

    const inStatus = input?.status;
    const apStatus = appointment?.status;

    const s1 = inStatus ?? apStatus;
    const s2 = s1 ?? ctx?.appointmentStatus;

    L.debug('refresh()', tag, 'status:', { s1, s2, inStatus, apStatus });

    const result = {
      appointmentBgn,
      appointmentId,
      appointmentStatus: AP_STATUS.finished === s1 ? AP_STATUS.needsPayment : s2 ?? AP_STATUS.needsPayment,
      cutoff,
      future,
      past: !future,
      deleteBeAll: ctx?.deleteBeAll ?? input?.deleteBeAll,
      updateBeStatus: ctx?.updateBeStatus ?? input?.updateBeStatus,
      updatedAt: nowAsIso(),
    };

    L.debug('refresh()', tag, ':', result, '<-', ctx, '+', input ?? appointment ?? void 1, '|', $);

    return result;
  };

  return refresh;
};

const actionInit = assign(refreshFor('initMachine'));

const actionDeleteOne = assign(() => {
  const now = nowAsIso();
  return { deletedAt: now, updatedAt: now };
});

const actionDeleteAll = assign(($, params) => {
  L.debug(APM_ACTION.deleteAll, '()', $, params);
  const ctxDeleteBeAll = $?.context?.deleteBeAll;

  L.info(APM_ACTION.deleteAll, '()', 'Calling deleteBeAll...');
  call(`${APM_ACTION.deleteAll}() > ctxDeleteBeAll()`, ctxDeleteBeAll);

  const now = nowAsIso();
  return { deletedAt: now, updatedAt: now };
});

const actionUpdateStatus = assign(($, params) => {
  const FTAG = `${APM_ACTION.updateStatus}()`;

  const ctx = $?.context;
  const eventType = $?.event?.type;

  const future = params?.future ?? ctx?.future;
  const past = params?.past ?? ctx?.past;

  const ctxAppointmentStatus = ctx?.appointmentStatus;
  const ctxUpdateBeStatus = ctx?.updateBeStatus;
  const appointmentId = ctx?.appointmentId;

  const status = fromAnyToTrimmed(params?.status);

  const appointmentStatus = AP_STATUS.finished === status ? AP_STATUS.needsPayment : status ?? ctxAppointmentStatus;

  const confirmations = { isUnconfirmedTransition: false, isNoShowTransition: false };
  const result = { ...confirmations, appointmentStatus, future, past, updatedAt: nowAsIso() };
  const extraInfo = { $, status, ctxAppointmentStatus, appointmentStatus, ctxUpdateBeStatus };

  L.debug(FTAG, eventType, '->', result, '<-', ctx, '+', params, extraInfo);

  if (APM_INIT_XSTATE_EVENT !== eventType && appointmentStatus && appointmentStatus !== ctxAppointmentStatus) {
    L.info(FTAG, 'Calling updateBeStatus with appointmentStatus:', appointmentStatus, '...');
    const event = { status: appointmentStatus, appointmentId };
    call(`${FTAG} > updateBeStatus()`, ctxUpdateBeStatus, event);
  } else {
    L.debug(FTAG, 'Not calling updateBeStatus with appointmentStatus:', appointmentStatus);
  }

  return result;
});

const actionDisplayConfirmation = assign(($, params) => {
  const ctx = $?.context;
  const eventType = $?.event?.type;

  const fromState = params?.fromState;
  const toState = params?.toState;
  const fromStatus = AP_STATUS_FOR_STATE[fromState];
  const toStatus = AP_STATUS_FOR_STATE[toState];

  const isNoShowTransition = APM_STATE.pns === toState;
  const isUnconfirmedTransition = !isNoShowTransition; // these are exclusive with each other

  const result = { fromState, toState, fromStatus, toStatus, isUnconfirmedTransition, isNoShowTransition };
  L.debug(APM_ACTION.displayConfirmation, '()', eventType, '->', result, '<-', ctx, '+', params, '|', $);

  return result;
});

const guardIsPast = ($) => isTimeBefore($?.context?.appointmentBgn, $?.context?.cutoff);
const guardIsFuture = ($) => isTimeAfterOrEqual($?.context?.appointmentBgn, $?.context?.cutoff);

const guardIsStatus = ($, params) => {
  const status = fromAnyToTrimmed(params);
  return !!status && status === $?.context?.appointmentStatus;
};

const guardIsSkip = ($, params) => {
  const status = fromAnyToTrimmed(params);
  return !!status && status === $?.context?.appointmentStatus;
};

const guardIsNowPast = ($) => {
  const bgn = $?.context?.appointmentBgn;
  const cutoff = fromAnyDateToIso($?.event?.cutoff) || fromAnyDateToIso($?.context?.cutoff);

  return isTimeSameDay(bgn, cutoff) || isTimeBefore(bgn, cutoff);
};

const confirmationBetween = (fromState, toState) => ({
  description: `Needs confirmation to transition from ${fromState} to ${toState}`,
  entry: [{ type: APM_ACTION.displayConfirmation, params: { fromState, toState } }],
  on: {
    [APM_EVENT.continue]: { target: toState },
    [APM_EVENT.revert]: { target: fromState },
  },
});

// noinspection JSCheckFunctionSignatures
const gnf = (status, target) => ({
  target,
  guard: and([APM_GUARD.isFuture, { type: APM_GUARD.isStatus, params: status }]),
});
// noinspection JSCheckFunctionSignatures
const gnp = (status, target) => ({
  target,
  guard: and([APM_GUARD.isPast, { type: APM_GUARD.isStatus, params: status }]),
});

// noinspection JSCheckFunctionSignatures
const gsf = (status, target) => ({
  target: APM_DOT + target,
  guard: and([APM_GUARD.isFuture, { type: APM_GUARD.isSkip, params: status }]),
});

// noinspection JSCheckFunctionSignatures
const gsp = (status, target) => ({
  target: APM_DOT + target,
  guard: and([APM_GUARD.isPast, { type: APM_GUARD.isSkip, params: status }]),
});

const gns = (status, target) => ({ target, guard: { type: APM_GUARD.isStatus, params: status } });
const gss = (status, target) => ({ target: APM_DOT + target, guard: { type: APM_GUARD.isSkip, params: status } });

const INITS = [
  gnf(AP_STATUS.finished, APM_STATE.fnp),
  gns(AP_STATUS.finished, APM_STATE.pnp),
  gns(AP_STATUS.paid, APM_STATE.pco),
  gns(AP_STATUS.deleted, APM_STATE.del),
  gnp(AP_STATUS.arrived, APM_STATE.par),
  gnp(AP_STATUS.canceled, APM_STATE.pca),
  gnp(AP_STATUS.confirmed, APM_STATE.pcf),
  gnp(AP_STATUS.customerUnverified, APM_STATE.pcu),
  gnp(AP_STATUS.needsPayment, APM_STATE.pnp),
  gnp(AP_STATUS.noShow, APM_STATE.pns),
  gnp(AP_STATUS.unconfirmed, APM_STATE.pun),
  gnf(AP_STATUS.arrived, APM_STATE.far),
  gnf(AP_STATUS.canceled, APM_STATE.fca),
  gnf(AP_STATUS.confirmed, APM_STATE.fcf),
  gnf(AP_STATUS.customerUnverified, APM_STATE.fcu),
  gnf(AP_STATUS.needsPayment, APM_STATE.fnp),
  gnf(AP_STATUS.unconfirmed, APM_STATE.fun),
  { guard: { type: APM_GUARD.isFuture }, target: APM_STATE.fnp },
  { target: APM_STATE.pnp },
];

const SKIPS = [
  gsf(AP_STATUS.finished, APM_STATE.fnp),
  gss(AP_STATUS.finished, APM_STATE.pnp),
  gss(AP_STATUS.paid, APM_STATE.pco),
  gss(AP_STATUS.deleted, APM_STATE.del),
  gsp(AP_STATUS.arrived, APM_STATE.par),
  gsp(AP_STATUS.canceled, APM_STATE.pca),
  gsp(AP_STATUS.confirmed, APM_STATE.pcf),
  gsp(AP_STATUS.customerUnverified, APM_STATE.pcu),
  gsp(AP_STATUS.needsPayment, APM_STATE.pnp),
  gsp(AP_STATUS.noShow, APM_STATE.pns),
  gsp(AP_STATUS.unconfirmed, APM_STATE.pun),
  gsf(AP_STATUS.arrived, APM_STATE.far),
  gsf(AP_STATUS.canceled, APM_STATE.fca),
  gsf(AP_STATUS.confirmed, APM_STATE.fcf),
  gsf(AP_STATUS.customerUnverified, APM_STATE.fcu),
  gsf(AP_STATUS.needsPayment, APM_STATE.fnp),
  gsf(AP_STATUS.unconfirmed, APM_STATE.fun),
  { guard: { type: APM_GUARD.isFuture }, target: APM_DOT + APM_STATE.fnp },
  { target: APM_DOT + APM_STATE.pnp },
];

L.debug('()', 'guarded:', INITS, SKIPS);

const configureContext = ($) => {
  L.debug('appointmentMachineConfiguration.context()', $);
  const { bgn, appointmentId, status } = $?.input ?? {};
  return { appointmentBgn: bgn, appointmentId, appointmentStatus: status, updatedAt: nowAsIso() };
};

const configureOutput = ($) => ({
  appointmentId: $?.context?.appointmentId,
  settledAt: nowAsIso(),
});

const APM_GLOBAL_TRANSITIONS = {
  [APM_EVENT.delOne]: { target: APM_DOT + APM_STATE.delOne },
  [APM_EVENT.delAll]: { target: APM_DOT + APM_STATE.delAll },
  [APM_EVENT.refresh]: { actions: assign(refreshFor('refreshEvent')), internal: true },
  [APM_EVENT.skip]: SKIPS,
};

const APM_STATES = {
  [APM_INIT_STATE]: { description: 'init', always: INITS },

  [APM_STATE.pcu2pns]: confirmationBetween(APM_STATE.pcu, APM_STATE.pns),
  [APM_STATE.pnp2pns]: confirmationBetween(APM_STATE.pnp, APM_STATE.pns),
  [APM_STATE.pun2pns]: confirmationBetween(APM_STATE.pun, APM_STATE.pns),
  [APM_STATE.pcf2pns]: confirmationBetween(APM_STATE.pcf, APM_STATE.pns),
  [APM_STATE.par2pns]: confirmationBetween(APM_STATE.par, APM_STATE.pns),

  [APM_STATE.pca2par]: confirmationBetween(APM_STATE.pca, APM_STATE.par),
  [APM_STATE.pca2pcf]: confirmationBetween(APM_STATE.pca, APM_STATE.pcf),
  [APM_STATE.pca2pun]: confirmationBetween(APM_STATE.pca, APM_STATE.pun),

  [APM_STATE.pns2par]: confirmationBetween(APM_STATE.pns, APM_STATE.par),
  [APM_STATE.pns2pcf]: confirmationBetween(APM_STATE.pns, APM_STATE.pcf),
  [APM_STATE.pns2pun]: confirmationBetween(APM_STATE.pns, APM_STATE.pun),

  [APM_STATE.fca2par]: confirmationBetween(APM_STATE.fca, APM_STATE.par),
  [APM_STATE.fca2pcf]: confirmationBetween(APM_STATE.fca, APM_STATE.pcf),
  [APM_STATE.fca2pun]: confirmationBetween(APM_STATE.fca, APM_STATE.pun),

  [APM_STATE.pnp]: {
    description: 'Past, Needs Payment',
    tags: [APM_TIME.past, AP_STATUS.needsPayment],
    entry: [{ type: APM_ACTION.updateStatus, params: { status: AP_STATUS.needsPayment, past: true, future: false } }],
    on: {
      [APM_EVENT.arrived]: { target: APM_STATE.par },
      [APM_EVENT.cancel]: { target: APM_STATE.pca },
      [APM_EVENT.confirm]: { target: APM_STATE.pcf },
      [APM_EVENT.noShow]: { target: APM_STATE.pnp2pns },
    },
  },

  [APM_STATE.fnp]: {
    description: 'Future, Needs Payment',
    tags: [APM_TIME.future, AP_STATUS.needsPayment],
    entry: [{ type: APM_ACTION.updateStatus, params: { status: AP_STATUS.needsPayment, past: false, future: true } }],
  },

  [APM_STATE.pcu]: {
    description: 'Past, Customer Unverified',
    tags: [APM_TIME.past, AP_STATUS.customerUnverified],
    entry: [{ type: APM_ACTION.updateStatus, params: { status: AP_STATUS.customerUnverified, past: true, future: false } }],
    on: {
      [APM_EVENT.arrived]: { target: APM_STATE.par },
      [APM_EVENT.cancel]: { target: APM_STATE.pca },
      [APM_EVENT.confirm]: { target: APM_STATE.pcf },
      [APM_EVENT.noShow]: { target: APM_STATE.pcu2pns },
    },
  },

  [APM_STATE.pun]: {
    description: 'Past, Unconfirmed',
    tags: [APM_TIME.past, AP_STATUS.unconfirmed],
    entry: [{ type: APM_ACTION.updateStatus, params: { status: AP_STATUS.unconfirmed, past: true, future: false } }],
    on: {
      [APM_EVENT.arrived]: { target: APM_STATE.par },
      [APM_EVENT.cancel]: { target: APM_STATE.pca },
      [APM_EVENT.confirm]: { target: APM_STATE.pcf },
      [APM_EVENT.noShow]: { target: APM_STATE.pun2pns },
    },
  },

  [APM_STATE.pca]: {
    description: 'Past, Cancelled',
    tags: [APM_TIME.past, AP_STATUS.canceled],
    entry: [{ type: APM_ACTION.updateStatus, params: { status: AP_STATUS.canceled, past: true, future: false } }],
    on: {
      [APM_EVENT.arrived]: { target: APM_STATE.pca2par },
      [APM_EVENT.cancel]: { target: APM_STATE.pca },
      [APM_EVENT.confirm]: { target: APM_STATE.pcf },
    },
  },

  [APM_STATE.pcf]: {
    description: 'Past, Confirmed',
    tags: [APM_TIME.past, AP_STATUS.confirmed],
    entry: [{ type: APM_ACTION.updateStatus, params: { status: AP_STATUS.confirmed, past: true, future: false } }],
    on: {
      [APM_EVENT.arrived]: { target: APM_STATE.par },
      [APM_EVENT.cancel]: { target: APM_STATE.pca },
      [APM_EVENT.confirm]: { target: APM_STATE.pun },
      [APM_EVENT.noShow]: { target: APM_STATE.pcf2pns },
    },
  },

  [APM_STATE.par]: {
    description: 'Past, Arrived',
    tags: [APM_TIME.past, AP_STATUS.arrived],
    entry: [{ type: APM_ACTION.updateStatus, params: { status: AP_STATUS.arrived, past: true, future: false } }],
    on: {
      [APM_EVENT.arrived]: { target: APM_STATE.pun },
      [APM_EVENT.confirm]: { target: APM_STATE.pcf },
      [APM_EVENT.noShow]: { target: APM_STATE.par2pns },
    },
  },

  [APM_STATE.pns]: {
    description: 'Past, No Show',
    tags: [APM_TIME.past, AP_STATUS.noShow],
    entry: [{ type: APM_ACTION.updateStatus, params: { status: AP_STATUS.noShow, past: true, future: false } }],
    on: {
      [APM_EVENT.arrived]: { target: APM_STATE.pns2par },
      [APM_EVENT.confirm]: { target: APM_STATE.pns2pcf },
      [APM_EVENT.noShow]: { target: APM_STATE.pun },
    },
  },

  [APM_STATE.fcu]: {
    description: 'Future, Customer Unverified',
    tags: [APM_TIME.future, AP_STATUS.customerUnverified],
    entry: [{ type: APM_ACTION.updateStatus, params: { status: AP_STATUS.customerUnverified, past: false, future: true } }],
    on: {
      [APM_EVENT.cancel]: { target: APM_STATE.fca },
      [APM_EVENT.confirm]: { target: APM_STATE.fcf },
      [APM_EVENT.tick]: { guard: APM_GUARD.isNowPast, target: APM_STATE.pcu },
    },
  },

  [APM_STATE.fun]: {
    description: 'Future, Unconfirmed',
    tags: [APM_TIME.future, AP_STATUS.unconfirmed],
    entry: [{ type: APM_ACTION.updateStatus, params: { status: AP_STATUS.unconfirmed, past: false, future: true } }],
    on: {
      [APM_EVENT.cancel]: { target: APM_STATE.fca },
      [APM_EVENT.confirm]: { target: APM_STATE.fcf },
      [APM_EVENT.tick]: { guard: APM_GUARD.isNowPast, target: APM_STATE.pun },
    },
  },

  [APM_STATE.fca]: {
    description: 'Future, Cancelled',
    tags: [APM_TIME.future, AP_STATUS.canceled],
    entry: [{ type: APM_ACTION.updateStatus, params: { status: AP_STATUS.canceled, past: false, future: true } }],
    on: {
      [APM_EVENT.cancel]: { target: APM_STATE.fun },
      [APM_EVENT.confirm]: { target: APM_STATE.fcf },
      [APM_EVENT.tick]: { guard: APM_GUARD.isNowPast, target: APM_STATE.pca },
    },
  },

  [APM_STATE.fcf]: {
    description: 'Future, Confirmed',
    tags: [APM_TIME.future, AP_STATUS.confirmed],
    entry: [{ type: APM_ACTION.updateStatus, params: { status: AP_STATUS.confirmed, past: false, future: true } }],
    on: {
      [APM_EVENT.cancel]: { target: APM_STATE.fca },
      [APM_EVENT.confirm]: { target: APM_STATE.fun },
      [APM_EVENT.tick]: { guard: APM_GUARD.isNowPast, target: APM_STATE.pcf },
    },
  },

  [APM_STATE.far]: {
    description: 'Future, Arrived',
    tags: [APM_TIME.future, AP_STATUS.arrived],
    entry: [{ type: APM_ACTION.updateStatus, params: { status: AP_STATUS.arrived, past: false, future: true } }],
    on: {
      [APM_EVENT.far]: { target: APM_STATE.fun },
      [APM_EVENT.tick]: { guard: APM_GUARD.isNowPast, target: APM_STATE.par },
    },
  },

  [APM_STATE.pco]: {
    // one of: DP (deposited), ND (no deposit), RE (deposit requested)
    description: 'Past, Checked Out',
    tags: [APM_TIME.past, AP_STATUS.paid],
    entry: [{ type: APM_ACTION.updateStatus, params: { status: AP_STATUS.paid, past: true, future: false } }],
  },

  [APM_STATE.delOne]: {
    description: 'Deleting One',
    entry: [{ type: APM_ACTION.deleteOne }, { type: APM_ACTION.updateStatus, params: { status: AP_STATUS.deleted } }],
    always: [{ target: APM_STATE.del }],
  },

  [APM_STATE.delAll]: {
    description: 'Deleting All',
    entry: [{ type: APM_ACTION.deleteAll }],
    always: [{ target: APM_STATE.del }],
  },

  [APM_STATE.del]: {
    description: 'Deleted',
  },
};

export const appointmentMachineSetup = {
  actions: {
    [APM_ACTION.deleteOne]: actionDeleteOne,
    [APM_ACTION.deleteAll]: actionDeleteAll,
    [APM_ACTION.init]: actionInit,
    [APM_ACTION.updateStatus]: actionUpdateStatus,
    [APM_ACTION.displayConfirmation]: actionDisplayConfirmation,
  },

  actors: {},

  guards: {
    [APM_GUARD.isPast]: guardIsPast,
    [APM_GUARD.isFuture]: guardIsFuture,
    [APM_GUARD.isStatus]: guardIsStatus,
    [APM_GUARD.isSkip]: guardIsSkip,
    [APM_GUARD.isNowPast]: guardIsNowPast,
  },

  delays: {},
};

export const appointmentMachineConfiguration = {
  id: 'appointmentMachine',
  initial: APM_INIT_STATE,
  context: configureContext,
  entry: APM_INIT_ENTRY,
  output: configureOutput,
  on: APM_GLOBAL_TRANSITIONS,
  states: APM_STATES,
};

export const appointmentMachine = setup(appointmentMachineSetup).createMachine(appointmentMachineConfiguration);
